import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';

import { Logo } from '@/components/shared/Logo/Logo';
import { useCanSchedule } from '@/features/scheduling/hooks/useCanSchedule';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils';
import { generateNavItems } from '../AppNavbar/AppNavbar';

import { NotificationsPopover } from '@/features/notifications/components/NotificationsPopover/NotificationsPopover';

interface SidebarItemProps {
  label: string;
  href: string;
  iconSrc: string;
  iconFilledSrc: string;
  className?: string;
}

const SidebarItem = ({ iconSrc, iconFilledSrc, label, href, className }: SidebarItemProps) => {
  const isActive = useMatch(href);

  return (
    <li className='flex w-full'>
      <Link
        to={href}
        className={cn('w-full flex-1 rounded-lg px-2.5 py-3', isActive && 'bg-fill-accent', className)}
        aria-current={isActive ? 'page' : undefined}
      >
        <div className='flex items-center gap-3'>
          <img src={isActive ? iconFilledSrc : iconSrc} alt='' className='size-5' aria-hidden />
          <span className={cn(isActive && 'text-text-brand-primary text-body-m-semibold')}>{label}</span>
        </div>
      </Link>
    </li>
  );
};

export const APP_SIDEBAR_WIDTH_IN_PX = 264;

interface AppSidebarProps {
  style?: React.CSSProperties;
}

export const AppSidebar = ({ style }: AppSidebarProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'appLayout.navigation' });

  const { userData } = useAuthStore();
  const hasExternalScheduling = !!userData?.center?.externalSchedulingUrl;
  const { canSchedule } = useCanSchedule();
  const isSchedulingEnabled = canSchedule;

  const navItems = generateNavItems(t, isSchedulingEnabled, hasExternalScheduling);

  return (
    <aside
      aria-label={t('ariaLabel')}
      className='fixed top-0 left-0 z-10 flex h-full flex-col items-start gap-10 p-8'
      style={{
        width: `${APP_SIDEBAR_WIDTH_IN_PX}px`,
        ...style,
      }}
    >
      <Logo variant='link' />
      <nav className='flex w-full flex-1 flex-col gap-12'>
        <NotificationsPopover />
        <ul className='flex flex-1 flex-col gap-1'>
          {navItems?.map(({ label, href, iconSrc, iconFilledSrc }) => (
            <SidebarItem key={label} iconSrc={iconSrc} iconFilledSrc={iconFilledSrc} label={label} href={href} />
          ))}
        </ul>
      </nav>
    </aside>
  );
};
